import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';
import { Icon } from '../tokens';

// Comprime cada imagen en el cliente a <300KB ANTES de mandarla al backend.
// Reduce ancho máximo y usa WebWorker para no bloquear el hilo principal.
// El backend luego genera thumb/hero en WebP definitivos vía Sharp.
const COMPRESSION_OPTS = {
  maxSizeMB: 0.3,           // 300 KB tope
  maxWidthOrHeight: 1600,   // suficiente para el hero (1200) con margen
  useWebWorker: true,
  initialQuality: 0.82,
};

// Componente controlado: el padre maneja `newFiles` y la lista de existentes
// con sus removidas. Aquí sólo encapsulamos la zona de drop + compresión.
export default function Dropzone({
  newFiles, setNewFiles,
  existingImages = [],
  removedExisting = [],
  onRemoveExisting,
  maxFiles = 6,
}) {
  const { t } = useTranslation();
  const [previews, setPreviews] = useState([]);
  const [compressing, setCompressing] = useState(0);
  const [error, setError] = useState('');

  // Mantener previews sincronizados con newFiles + liberar object URLs.
  useEffect(() => {
    const urls = newFiles.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [newFiles]);

  const kept = existingImages.filter((u) => !removedExisting.includes(u));
  const total = kept.length + newFiles.length;
  const remainingSlots = Math.max(0, maxFiles - total);

  const onDrop = useCallback(async (accepted) => {
    if (!accepted.length) return;
    setError('');
    setCompressing((n) => n + accepted.length);
    try {
      const compressed = await Promise.all(
        accepted.map(async (file) => {
          // Si ya viene <300KB lo dejamos pasar (ahorra CPU del cliente).
          if (file.size <= COMPRESSION_OPTS.maxSizeMB * 1024 * 1024) return file;
          try { return await imageCompression(file, COMPRESSION_OPTS); }
          catch { return file; }
        }),
      );
      setNewFiles((prev) => [...prev, ...compressed].slice(0, maxFiles));
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setCompressing((n) => Math.max(0, n - accepted.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxFiles, setNewFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    maxSize: 20 * 1024 * 1024, // 20MB en cliente; comprimimos a <300KB antes de subir
    maxFiles: remainingSlots,
    disabled: remainingSlots === 0 || compressing > 0,
  });

  const removeNew = (i) => setNewFiles((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <>
      <div {...getRootProps()} className={`dropzone${isDragActive ? ' dropzone--over' : ''}`}>
        <input {...getInputProps()} />
        {total === 0 ? (
          <div className="dz-empty">
            <div className="dz-icon">{Icon.cloud(26)}</div>
            <p className="dz-title">{t('upload.drop_title')}</p>
            <p className="dz-sub">{t('upload.drop_sub')}</p>
            <div className="dz-add">+</div>
          </div>
        ) : (
          <div className="dz-previews">
            {kept.map((url) => (
              <div key={url} className="dz-thumb">
                <img src={url} alt="" />
                <button type="button" className="dz-remove" onClick={(e) => { e.stopPropagation(); onRemoveExisting?.(url); }}>✕</button>
              </div>
            ))}
            {previews.map((src, i) => (
              <div key={`new-${i}`} className="dz-thumb">
                <img src={src} alt="" />
                <button type="button" className="dz-remove" onClick={(e) => { e.stopPropagation(); removeNew(i); }}>✕</button>
              </div>
            ))}
            {total < maxFiles && <div className="dz-add-more">+</div>}
          </div>
        )}
      </div>
      {compressing > 0 && (
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: -16, marginBottom: 16 }}>
          {t('upload.compressing', { defaultValue: 'Optimizando imágenes…' })}
        </p>
      )}
      {error && <p style={{ fontSize: 12, color: '#ef4444', marginTop: -16, marginBottom: 16 }}>{error}</p>}
    </>
  );
}
